import type { Station, StationKind } from '@/state/types';
import {
  hasExtensionFlag,
  sumExtensionHoldingCells,
  sumExtensionPatientCapacity,
  sumExtensionSlots,
  sumExtensionUpkeep,
} from './stationExtensions';
import {
  POLICE_REVIER_TIERS,
  RIOT_POLICE_TIERS,
  SEK_BASE_TIERS,
  FEDERAL_POLICE_TIERS,
  isPoliceStationKind,
  type PoliceStationTier,
} from './policeStations';

/**
 * Block B — Tier-based station progression with offizielle Wiki-Werte.
 * Replaces the flat-price model from Phase 4.
 *
 * Hospital tiers use `patientCapacity` instead of `vehicleSlots`:
 * Hospitals are NPC, store no vehicles. The slot in the wiki maps to
 * patient-treatment capacity (Phase 5: blocks RTW unloading when full).
 */

interface BaseTier {
  level: number;
  /** Cost in € to build/upgrade *to* this tier. */
  cost: number;
  /** Wiki real-time hours; gets converted via BUILD_TIME_REAL_RATIO. */
  buildTimeHours: number;
  /** Daily upkeep in € (real-day). */
  upkeepPerDay: number;
  /** Phase 8: tier includes a built-in workshop (no separate extension needed). */
  builtInWorkshop?: boolean;
}

export interface VehicleStationTier extends BaseTier {
  vehicleSlots: number;
}

export interface HospitalTier extends BaseTier {
  patientCapacity: number;
}

/* ───────────────────────── Berufsfeuerwehr (BF) ───────────────────────── */
export const BF_TIERS: ReadonlyArray<VehicleStationTier> = [
  { level: 1, cost: 5_000_000, buildTimeHours: 12,  upkeepPerDay: 1_000, vehicleSlots: 5  },
  { level: 2, cost: 1_000_000, buildTimeHours: 24,  upkeepPerDay: 1_300, vehicleSlots: 7  },
  { level: 3, cost: 1_750_000, buildTimeHours: 48,  upkeepPerDay: 1_650, vehicleSlots: 9  },
  // Phase 8.2/3 — BF Hauptwachen ab Tier 4 haben eingebaute Werkstatt.
  { level: 4, cost: 2_500_000, buildTimeHours: 72,  upkeepPerDay: 2_000, vehicleSlots: 12, builtInWorkshop: true },
  { level: 5, cost: 3_000_000, buildTimeHours: 120, upkeepPerDay: 2_500, vehicleSlots: 16, builtInWorkshop: true },
  { level: 6, cost: 3_500_000, buildTimeHours: 168, upkeepPerDay: 2_800, vehicleSlots: 20, builtInWorkshop: true },
  { level: 7, cost: 4_000_000, buildTimeHours: 168, upkeepPerDay: 3_200, vehicleSlots: 26, builtInWorkshop: true },
];

/* ───────────────────────── Freiwillige Feuerwehr (FF) ─────────────────── */
export const FF_TIERS: ReadonlyArray<VehicleStationTier> = [
  { level: 1, cost: 2_500_000, buildTimeHours: 1,   upkeepPerDay: 500,   vehicleSlots: 2  },
  { level: 2, cost: 600_000,   buildTimeHours: 4,   upkeepPerDay: 650,   vehicleSlots: 3  },
  { level: 3, cost: 800_000,   buildTimeHours: 12,  upkeepPerDay: 820,   vehicleSlots: 4  },
  { level: 4, cost: 1_000_000, buildTimeHours: 24,  upkeepPerDay: 1_000, vehicleSlots: 5  },
  { level: 5, cost: 1_500_000, buildTimeHours: 72,  upkeepPerDay: 1_500, vehicleSlots: 7  },
  { level: 6, cost: 2_000_000, buildTimeHours: 120, upkeepPerDay: 1_800, vehicleSlots: 10 },
  { level: 7, cost: 2_500_000, buildTimeHours: 168, upkeepPerDay: 2_000, vehicleSlots: 15 },
];

/* ───────────────────────── Rettungsdienstwache (RD) ───────────────────── */
export const EMS_TIERS: ReadonlyArray<VehicleStationTier> = [
  { level: 1, cost: 1_500_000, buildTimeHours: 1,  upkeepPerDay: 350, vehicleSlots: 2 },
  { level: 2, cost: 300_000,   buildTimeHours: 4,  upkeepPerDay: 500, vehicleSlots: 3 },
  { level: 3, cost: 500_000,   buildTimeHours: 12, upkeepPerDay: 650, vehicleSlots: 4 },
  { level: 4, cost: 750_000,   buildTimeHours: 24, upkeepPerDay: 800, vehicleSlots: 5 },
];

/* ─────────────────────────── Krankenhaus ──────────────────────────────── */
export const HOSPITAL_TIERS: ReadonlyArray<HospitalTier> = [
  { level: 1, cost: 0,       buildTimeHours: 0, upkeepPerDay: 15_000, patientCapacity: 1 },
  { level: 2, cost: 250_000, buildTimeHours: 4, upkeepPerDay: 25_000, patientCapacity: 1 },
  { level: 3, cost: 350_000, buildTimeHours: 6, upkeepPerDay: 35_000, patientCapacity: 2 },
  { level: 4, cost: 500_000, buildTimeHours: 8, upkeepPerDay: 50_000, patientCapacity: 2 },
];

/** Minimum distance (meters) between two stations of the same kind. Hospitals exempt. */
export const MIN_STATION_DISTANCE_M = 500;

/** Type guard helper for the common case (vehicle stations vs hospital/courthouse). */
export function isVehicleStationKind(kind: StationKind): boolean {
  return kind !== 'hospital' && kind !== 'courthouse';
}

export interface StationSpec {
  kind: StationKind;
  label: string;
  shortLabel: string;
  /** True for BF/FF/EMS — hospital is NPC and stores no purchased vehicles. */
  supportsVehicles: boolean;
  /** Short description shown in the type picker. */
  description: string;
  /** HEX color for map markers / accents. */
  markerColor: string;
}

export const STATION_SPECS: Record<StationKind, StationSpec> = {
  'bf-station': {
    kind: 'bf-station',
    label: 'Berufsfeuerwehr',
    shortLabel: 'BF',
    supportsVehicles: true,
    description: 'Hauptwache mit großen Slots, kurze Alarmierungszeit (45–90 s).',
    markerColor: '#b91c1c',
  },
  'ff-station': {
    kind: 'ff-station',
    label: 'Freiwillige Feuerwehr',
    shortLabel: 'FF',
    supportsVehicles: true,
    description: 'Günstig im Bau, längere Alarmierungszeit (180–300 s).',
    markerColor: '#dc2626',
  },
  'ems-station': {
    kind: 'ems-station',
    label: 'Rettungswache',
    shortLabel: 'RW',
    supportsVehicles: true,
    description: 'Rettungsdienst-Standort für RTW, NEF, KTW.',
    markerColor: '#f97316',
  },
  hospital: {
    kind: 'hospital',
    label: 'Krankenhaus',
    shortLabel: 'KH',
    supportsVehicles: false,
    description: 'Nimmt Patienten auf. Keine eigenen Fahrzeuge.',
    markerColor: '#374151',
  },
  // Phase 10.1 — Polizei
  'police-station': {
    kind: 'police-station',
    label: 'Polizeirevier',
    shortLabel: 'PR',
    supportsVehicles: true,
    description: 'Landespolizei: Streifendienst, schnelle Alarmierung (60–120 s).',
    markerColor: '#1d4ed8',
  },
  'riot-police-station': {
    kind: 'riot-police-station',
    label: 'Bereitschaftspolizei',
    shortLabel: 'BePo',
    supportsVehicles: true,
    description: 'Geschlossene Einheit, Wasserwerfer-fähig. Längere Alarmierung.',
    markerColor: '#1e40af',
  },
  'sek-base': {
    kind: 'sek-base',
    label: 'SEK-Basis',
    shortLabel: 'SEK',
    supportsVehicles: true,
    description: 'Spezialeinheit. Sehr lange Alarmierung (15–25 min).',
    markerColor: '#312e81',
  },
  'federal-police-station': {
    kind: 'federal-police-station',
    label: 'Bundespolizei',
    shortLabel: 'BPOL',
    supportsVehicles: true,
    description: 'Bahnhöfe / Flughäfen / Grenzen. Hubschrauber möglich.',
    markerColor: '#0c4a6e',
  },
  // Phase 10.5 — NPC courthouse for arrest transfers.
  courthouse: {
    kind: 'courthouse',
    label: 'Gericht',
    shortLabel: 'GE',
    supportsVehicles: false,
    description: 'NPC: nimmt Festgenommene zur Strafverfolgung auf.',
    markerColor: '#475569',
  },
};

export const STATION_SPEC_LIST: StationSpec[] = [
  STATION_SPECS['bf-station'],
  STATION_SPECS['ff-station'],
  STATION_SPECS['ems-station'],
  STATION_SPECS.hospital,
  STATION_SPECS['police-station'],
  STATION_SPECS['riot-police-station'],
  STATION_SPECS['sek-base'],
  STATION_SPECS['federal-police-station'],
];

/* ─────────────────────── Tier accessors ───────────────────────────────── */

/**
 * Phase 10.1 — Police tiers expose `vehicleSlots`, so the structural type is
 * compatible with `VehicleStationTier`. We type the union loosely here to keep
 * legacy call sites compiling.
 */
export function getTiers(
  kind: StationKind,
): ReadonlyArray<VehicleStationTier | HospitalTier | PoliceStationTier> {
  switch (kind) {
    case 'bf-station':
      return BF_TIERS;
    case 'ff-station':
      return FF_TIERS;
    case 'ems-station':
      return EMS_TIERS;
    case 'hospital':
      return HOSPITAL_TIERS;
    case 'police-station':
      return POLICE_REVIER_TIERS;
    case 'riot-police-station':
      return RIOT_POLICE_TIERS;
    case 'sek-base':
      return SEK_BASE_TIERS;
    case 'federal-police-station':
      return FEDERAL_POLICE_TIERS;
    case 'courthouse':
      return [];
  }
}

/** Number of tiers (= max level) for a kind. */
export function getMaxTierLevel(kind: StationKind): number {
  return getTiers(kind).length;
}

/** Tier object for a station's current level. Clamps to nearest valid tier. */
export function getCurrentTier(
  kind: StationKind,
  level: number,
): VehicleStationTier | HospitalTier | PoliceStationTier {
  const tiers = getTiers(kind);
  const idx = Math.max(0, Math.min(tiers.length - 1, Math.floor(level) - 1));
  return tiers[idx];
}

/** Tier object for the next level (= cost/time to upgrade). Null at max. */
export function getNextTier(
  kind: StationKind,
  level: number,
): VehicleStationTier | HospitalTier | PoliceStationTier | null {
  const tiers = getTiers(kind);
  const nextIdx = Math.floor(level); // current level → next index
  if (nextIdx >= tiers.length) return null;
  return tiers[nextIdx];
}

/** Slot/capacity scalar for the current level (vehicle stations only). */
export function getVehicleSlots(kind: StationKind, level: number): number {
  if (!isVehicleStationKind(kind)) return 0;
  const tier = getCurrentTier(kind, level);
  // VehicleStationTier and PoliceStationTier both expose `vehicleSlots`.
  if (tier && 'vehicleSlots' in tier) return tier.vehicleSlots;
  return 0;
}

/** Patient capacity for hospitals at the current level. */
export function getPatientCapacity(kind: StationKind, level: number): number {
  if (kind !== 'hospital') return 0;
  return (getCurrentTier(kind, level) as HospitalTier).patientCapacity;
}

/** Daily upkeep at the current level. */
export function getStationUpkeep(kind: StationKind, level: number): number {
  return getCurrentTier(kind, level).upkeepPerDay;
}

/** German-preferred station naming. Counters per kind handled by caller. */
export function defaultStationName(kind: StationKind, index: number): string {
  const prefix: Record<StationKind, string> = {
    'bf-station': 'BF',
    'ff-station': 'FF',
    'ems-station': 'Rettungswache',
    hospital: 'Klinikum',
    'police-station': 'Polizeirevier',
    'riot-police-station': 'Bereitschaftspolizei',
    'sek-base': 'SEK-Basis',
    'federal-police-station': 'Bundespolizei',
    courthouse: 'Gericht',
  };
  return `${prefix[kind]} ${index}`;
}

/** Phase 10 — Convenience: kind belongs to the police organisation? */
export const isPoliceStation = isPoliceStationKind;

/**
 * Phase 10.4.2 — Effective holding-cell capacity = base from tier + extension bonuses.
 * Returns 0 for non-police kinds.
 */
export function effectiveHoldingCells(station: Station): number {
  if (!isPoliceStationKind(station.kind)) return 0;
  const tier = getCurrentTier(station.kind, station.level) as PoliceStationTier;
  const base = tier?.holdingCells ?? 0;
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return base + sumExtensionHoldingCells(extIds);
}

/* ──────────── Phase 8.2 — combined tier + extension helpers ──────────── */

/** Effective vehicle slots = tier.vehicleSlots + sum of extension slot bonuses. */
export function effectiveVehicleSlots(station: Station): number {
  if (!isVehicleStationKind(station.kind)) return 0;
  const tierSlots = (getCurrentTier(station.kind, station.level) as VehicleStationTier)
    .vehicleSlots;
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return tierSlots + sumExtensionSlots(extIds);
}

/** Effective patient capacity (hospitals only). */
export function effectivePatientCapacity(station: Station): number {
  if (station.kind !== 'hospital') return 0;
  const base = (getCurrentTier(station.kind, station.level) as HospitalTier).patientCapacity;
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return base + sumExtensionPatientCapacity(extIds);
}

/** Effective daily upkeep = tier upkeep + sum of extension upkeeps. */
export function effectiveDailyUpkeep(station: Station): number {
  const tier = getCurrentTier(station.kind, station.level);
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return tier.upkeepPerDay + sumExtensionUpkeep(extIds);
}

/**
 * Phase 8.2/3 — Reparatur an dieser Wache möglich?
 * - BF-Tier mit `builtInWorkshop: true` → automatisch
 * - sonst nur, wenn die `allowsRepair`-Erweiterung installiert ist
 */
export function stationAllowsRepair(station: Station): boolean {
  const tier = getCurrentTier(station.kind, station.level);
  // Only fire-station tiers carry `builtInWorkshop`. Use a narrow `in` check.
  if (tier && 'builtInWorkshop' in tier && tier.builtInWorkshop) return true;
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return hasExtensionFlag(extIds, 'allowsRepair');
}

/**
 * Phase 8.2.2 — EMS vehicles (RTW/KTW/NEF) can only be stationed at:
 * - ems-station (always), OR
 * - ff-/bf-station with the "Rettungswachenanbau"-Erweiterung installed
 */
export function stationAllowsEmsVehicles(station: Station): boolean {
  if (station.kind === 'ems-station') return true;
  if (station.kind === 'hospital') return false;
  const extIds = (station.extensions ?? []).map((e) => e.extensionId);
  return hasExtensionFlag(extIds, 'allowsEmsVehicles');
}
